/* eslint-disable import/no-cycle */
import CloseIcon from "@mui/icons-material/Close";
import { AppBar, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import LinearProgress from "@mui/material/LinearProgress";
import Slide from "@mui/material/Slide";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import get from "lodash/get";
import upperFirst from "lodash/upperFirst";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { getRecordId } from "@utils/helpers";

import DotStepper from "../../../components/MUI/DotStepper";
import LoadingButton from "../../../components/MUI/LoadingButton";
import AuthService from "../../../services/authService";
import UserService from "../../../services/userService";
import { extractError, getName } from "../../../services/utils";
import { COLORS } from "../../../styles/colors";
import SignatureAdoptionComponent from "./SignatureAdoption/SignatureAdoptionComponent";
import SignatureDisclosure from "./SignatureDisclosure";

// Import your PDF worker – required by react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type FieldValues = Record<string, string>;

const titleCase = (str: string) => {
  return str.toLowerCase().split(" ").map(upperFirst).join(" ");
};

type DocumentField = {
  x: number;
  y: number;
  page: number;
  name: string;
  type: string;
  label: string;
  hidden: boolean;
};

type DocumentDetails = {
  id: string;
  status: string;
  documentId: string;
  documentName: string;
};

type ESignViewerProps = {
  title?: string;
  loading?: boolean;
  documentDetails: Partial<DocumentDetails>;
  onClose: () => void;
  onSigned?: () => void;
  showERSD: "once" | "all"; // Whether to show eSign Disclosure
};

const defaultValues = {
  name: "",
  date: dayjs().format(),
  signatureImage: "",
};

const ESignViewer = (props: ESignViewerProps) => {
  const id = getRecordId(get(props, "documentDetails"));
  const documentId = get(props, "documentDetails.documentId", "");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const authUser = AuthService.getAuth().user;
  const patientId = authUser.id;

  const [showDisclosure, setShowDisclosure] = useState(true);
  const [disclosureShown, setDisclosureShown] = useState(false);
  const [updatingDisclosure, setUpdatingDisclosure] = useState(false);
  const [signing, setSigning] = useState(false);

  const [documentLoading, setDocumentLoading] = useState(true);
  const [allowFieldDrag, setAllowFieldDrag] = useState(false); // dev only
  const [documentFields, setDocumentFields] = useState<DocumentField[]>([]);
  const [originalFields, setOriginalFields] = useState<DocumentField[]>([]);
  const [numPages, setNumPages] = useState<number>(0);
  const [loadingDocument, setLoadingDocument] = useState(true);
  const [scale, setScale] = useState(1);
  const [documentBuffer, setDocumentBuffer] = useState<string | null>(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(0);
  const [adoptedSignature, setAdoptedSignature] = useState<string>(() => "");
  const [fieldValues, setFieldValues] = useState<FieldValues>(() => ({
    ...defaultValues,
    name: getName(authUser, true),
  }));

  // These will store the dimensions of the first page of the PDF at scale=1
  const [originalPdfWidth, setOriginalPdfWidth] = useState<number | null>(null);
  const [originalPdfHeight, setOriginalPdfHeight] = useState<number | null>(
    null,
  );

  // PDF load success callback
  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    [],
  );

  // On each page render success, capture the original page width/height at scale=1
  const onPageRenderSuccess = useCallback(
    (page) => {
      const viewport = page.getViewport({ scale: 1 });
      // Store original width/height once if not already stored
      if (!originalPdfWidth || !originalPdfHeight) {
        setOriginalPdfWidth(viewport.width);
        setOriginalPdfHeight(viewport.height);
      }

      if (page?.pageNumber === 1) setLoadingDocument(false);
    },
    [originalPdfWidth, originalPdfHeight],
  );

  // Dynamically update scale based on container width and original PDF width
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && originalPdfWidth && originalPdfHeight) {
        const containerWidth = containerRef.current.offsetWidth;
        const aspectRatio = originalPdfHeight / originalPdfWidth;
        const containerHeight = containerWidth * aspectRatio;

        // Set container height based on the aspect ratio
        containerRef.current.style.height = `${containerHeight}px`;

        // Compute scale based on the container width and original PDF width
        setScale(containerWidth / originalPdfWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial calculation when component mounts
    return () => window.removeEventListener("resize", handleResize);
  }, [originalPdfWidth, originalPdfHeight]);

  useEffect(() => {
    if (!props.loading && documentId) {
      handDisclosurePrompt();
      getDocumentDetails();
    }
  }, [documentId, props.loading]);

  // Handle Disclosure logic
  const handDisclosurePrompt = () => {
    if (!disclosureShown && props.showERSD === "once") {
      setShowDisclosure(true);
      setDisclosureShown(true);
    } else if (props.showERSD === "all") setShowDisclosure(true);
  };
  // Handler for text/date input changes
  const handleFieldChange = (name: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  // Navigation Handlers
  const goToNextField = () => {
    setCurrentFieldIndex((prev) => Math.min(prev + 1, documentFields.length));
  };

  // Fetch document details
  const getDocumentDetails = () => {
    if (!documentLoading) setDocumentLoading(true);
    UserService.getSigningDocument({
      documentId,
    })
      .then((res) => {
        const fields = get(res, "data.fields", []);
        const signature = get(res, "data.signatureImage", "");
        const visibleFields = fields.filter((f: any) => !f?.hidden);
        setDocumentFields(visibleFields);
        setDocumentBuffer(res.data.documentBuffer);
        setOriginalFields(res.data.fields);
        setAdoptedSignature(signature);
        setFieldValues({
          ...defaultValues,
          name: getName(authUser, true),
        });
      })
      .finally(() => setDocumentLoading(false));
  };

  const handleDisclosureSubmit = () => {
    if (!updatingDisclosure) setUpdatingDisclosure(true);
    UserService.updateSignatureAudit({
      id,
      patientId,
      documentId,
      auditEvent: "ESIGN_CONSENT",
    }).finally(() => {
      setUpdatingDisclosure(false);
      setShowDisclosure(false);
    });
  };

  const finishSigning = async () => {
    setSigning(true);
    try {
      await UserService.processDocumentSignature({
        ...fieldValues,
        id,
        patientId,
        documentId,
      });

      // reset state
      setNumPages(0);
      setSigning(false);
      setCurrentFieldIndex(0);

      props.onSigned?.();
    } catch (error) {
      setSigning(false);
      extractError(error, "Unable to sign the document. Please try again!");
    }
  };

  const currentField = documentFields[currentFieldIndex] || {};

  // Scale the field positions based on the computed scale and original PDF coordinates
  const scalePosition = (
    x: number,
    y: number,
    width?: number,
    height?: number,
  ): { left: number; top: number; width?: number; height?: number } => {
    if (!originalPdfWidth || !originalPdfHeight) {
      return { left: x, top: y, width, height };
    }
    // Multiply coordinates by the current scale
    const left = x * scale;
    const top = y * scale;
    const scaledWidth = width ? width * scale : undefined;
    const scaledHeight = height ? height * scale : undefined;

    return { left, top, width: scaledWidth, height: scaledHeight };
  };

  // Render overlays on each page
  const renderOverlays = (pageIndex: number) => {
    const handleMouseDown = (e: React.MouseEvent, field: DocumentField) => {
      const startX = e.clientX;
      const startY = e.clientY;
      const { x, y } = field;

      const onMouseMove = (moveEvent: any) => {
        const deltaX = (moveEvent.clientX - startX) / scale;
        const deltaY = (moveEvent.clientY - startY) / scale;

        setOriginalFields((prev: DocumentField[]) => {
          const updatedFields = prev.map((f) =>
            f.name === field.name ? { ...f, x: x + deltaX, y: y + deltaY } : f,
          );
          return updatedFields;
        });
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    };

    return originalFields
      .filter((field) => field.page === pageIndex + 1)
      .map((field) => {
        const {
          left,
          top,
          width = 1,
          height = 1,
        } = scalePosition(
          field.x,
          field.y,
          200, // Assume a default max width for the signature field
          50, // Assume a default height for text originalFields
        );

        const commonStyle: any = {
          top,
          left,
          position: "absolute",
          borderRadius: "4px",
          fontSize: `${12 * scale}px`,
          cursor: allowFieldDrag ? "grab" : "default",
        };

        const mouseDownHandler: any = allowFieldDrag
          ? (e: any) => handleMouseDown(e, field)
          : undefined;
        if (field.type === "signature" && fieldValues.signatureImage) {
          return (
            <img
              key={field.name}
              id={`field-${field.name}`}
              src={fieldValues[field.name]}
              alt={`${field.label}`}
              onMouseDown={mouseDownHandler}
              style={{
                ...commonStyle,
                top: top - top * 0.03,
                height: height - 5,
                backgroundColor: COLORS.LIGHT_GREEN,
                border: "2px dashed green",
                objectFit: "contain",
              }}
            />
          );
        } else if (field.type !== "signature" && fieldValues[field.name]) {
          return (
            <Typography
              key={field.name}
              id={`field-${field.name}`}
              onMouseDown={mouseDownHandler}
              sx={{
                ...commonStyle,
                backgroundColor: COLORS.LIGHT_GREEN,
                border: "2px dashed green",
                padding: `${2 * scale}px ${4 * scale}px`, // Scale padding
              }}
            >
              {field.type === "date"
                ? dayjs(fieldValues[field.name]).format("MM/DD/YYYY")
                : fieldValues[field.name]}
            </Typography>
          );
        } else {
          return (
            <Typography
              key={field.name}
              id={`field-${field.name}`}
              onMouseDown={mouseDownHandler}
              sx={{
                ...commonStyle,
                backgroundColor: COLORS.LIGHT_GREY,
                border: `2px dashed ${COLORS.DARK_GREY}`,
                padding: `${2 * scale}px ${4 * scale}px`,
              }}
            >
              {field.label}
            </Typography>
          );
        }
      });
  };

  let content = null;
  if (showDisclosure) {
    content = (
      <SignatureDisclosure
        onSubmit={handleDisclosureSubmit}
        loading={updatingDisclosure}
      />
    );
  } else if (documentLoading || props.loading) {
    content = <CircularProgress />;
  } else {
    const signatureValue = adoptedSignature || fieldValues.signatureImage;

    content = (
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* PDF Display Area */}
        <Box
          id="controls-container"
          ref={containerRef}
          sx={{
            mb: 2,
            flex: 1,
            width: "100%",
            maxWidth: 700,
            borderRadius: "8px",
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
            backgroundColor: COLORS.WHITE,
          }}
        >
          {props.title && (
            <AppBar
              position="sticky"
              sx={{
                margin: "0 auto",
                background: COLORS.BLUE,
              }}
            >
              <Box display="flex" alignItems="center" paddingX={2}>
                <Typography
                  variant="body1"
                  textAlign="center"
                  padding={1}
                  flex={1}
                >
                  {props.title}
                </Typography>
                <IconButton
                  edge="start"
                  size="small"
                  onClick={props.onClose}
                  aria-label="close"
                >
                  <CloseIcon sx={{ color: "primary.contrastText" }} />
                </IconButton>
              </Box>
            </AppBar>
          )}
          <Document
            loading={<LinearProgress />}
            file={documentBuffer}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Box key={index} sx={{ position: "relative" }}>
                <Page
                  loading={<LinearProgress />}
                  pageNumber={index + 1}
                  scale={scale}
                  onRenderSuccess={onPageRenderSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
                {/* Overlays (documentFields) */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  {renderOverlays(index)}
                </Box>
                {index < numPages - 1 && (
                  <div
                    style={{
                      height: "20px",
                      width: "100%",
                      backgroundColor: "gray",
                    }}
                  />
                )}
              </Box>
            ))}
          </Document>
        </Box>

        {/* Field Editing Controls */}
        <Slide
          direction="up"
          in={!loadingDocument}
          mountOnEnter
          easing={{ enter: "ease-in-out" }}
          timeout={1000}
        >
          <div
            style={{
              maxWidth: 600,
              width: "90%",
              margin: "0 auto",
              position: "sticky",
              bottom: 0,
            }}
          >
            <Box
              sx={{
                padding: 2,
                width: "100%",
                display: "flex",
                borderRadius: 2,
                position: "relative",
                alignItems: "center",
                flexDirection: "column",
                backgroundColor: COLORS.WHITE,
                border: `1px solid ${COLORS.LIGHT_GREY}`,
                boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
              }}
            >
              {currentField.type !== "signature" && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {currentField.label || "Final Confirmation"}
                </Typography>
              )}

              {currentField.type === "signature" &&
                !adoptedSignature &&
                !fieldValues.signatureImage && (
                  <SignatureAdoptionComponent
                    onAdopt={(signature) => {
                      setFieldValues((prev) => ({
                        ...prev,
                        signatureImage: signature.dataURL,
                      }));

                      goToNextField();
                    }}
                  />
                )}

              {currentField.type && currentField.type !== "signature" && (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  inputRef={fieldRef}
                  onFocus={() => {
                    const el = document.getElementById("controls-container");
                    el?.scrollIntoView({
                      behavior: "smooth",
                      block: "end",
                    });
                  }}
                  sx={{
                    mb: 2,
                    maxWidth: 250,
                    backgroundColor: "white",
                    "& .MuiInputBase-input": {
                      textAlign: "center", // Align text to the center
                    },
                  }}
                  value={fieldValues[currentField.name] || ""}
                  type={currentField.type === "date" ? "date" : "text"}
                  label={
                    currentField.name === "name" ? "Enter Full Name" : "Date"
                  }
                  onChange={(e) =>
                    handleFieldChange(
                      currentField.name,
                      titleCase(e.target.value),
                    )
                  }
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {currentField.type !== "signature" &&
                  currentFieldIndex <= documentFields.length - 1 && (
                    <Button
                      fullWidth
                      size="medium"
                      variant="contained"
                      onClick={goToNextField}
                      sx={{ textTransform: "none" }}
                      disabled={!fieldValues[currentField.name]}
                    >
                      Continue
                    </Button>
                  )}

                {currentField.type === "signature" && signatureValue && (
                  <>
                    <img
                      src={signatureValue}
                      alt="Drawn Signature"
                      style={{
                        marginBottom: 10,
                        padding: 8,
                        maxWidth: "50%",
                        border: "1px dashed #ccc",
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        columnGap: 1,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        size="medium"
                        variant="contained"
                        sx={{ textTransform: "none" }}
                        onClick={() => {
                          handleFieldChange("signatureImage", signatureValue);
                          goToNextField();
                        }}
                      >
                        Sign and Continue
                      </Button>
                      <Button
                        size="medium"
                        variant="outlined"
                        sx={{ textTransform: "none" }}
                        onClick={() => {
                          setAdoptedSignature("");
                          handleFieldChange("signatureImage", "");
                        }}
                      >
                        Change
                      </Button>
                    </Box>
                  </>
                )}

                {signatureValue &&
                  currentFieldIndex === documentFields.length && (
                    <LoadingButton
                      fullWidth
                      size="medium"
                      loading={signing}
                      variant="contained"
                      sx={{ textTransform: "none" }}
                      onClick={finishSigning}
                    >
                      Confirm Signature
                    </LoadingButton>
                  )}

                <DotStepper
                  steps={3}
                  activeStep={currentFieldIndex}
                  disableForward={!fieldValues[currentField.name]}
                  onStepChange={(step) => setCurrentFieldIndex(step)}
                />
              </Box>
            </Box>
          </div>
        </Slide>
      </Container>
    );
  }
  return (
    <Dialog
      fullScreen
      open
      disablePortal
      id="esign-viewer"
      sx={
        documentLoading
          ? {
              "& .MuiPaper-root": {
                justifyContent: "center",
                alignItems: "center",
              },
            }
          : undefined
      }
      PaperProps={{ sx: { backgroundColor: "background.default" } }}
    >
      {content}
    </Dialog>
  );
};

export default ESignViewer;
